import { SUBSPEC_IDS } from '../data/subspecs.ts';
import { createNewSave } from '../model/save.ts';
import { SLOT_IDS, SLOT_MAX_RANK, type BuildState, type Rule, type SaveData, type SlotId, type SubSpecId } from '../model/types.ts';

const TOP_LEVEL = ['hero', 'gear', 'currencies', 'progress', 'idle'] as const;
const CURRENCIES = ['gold', 'forgeDust'] as const;
const RATE_KEYS = ['goldPerMin', 'xpPerMin', 'dustPerMin', 'dropRollsPerMin'] as const;

export class SaveDocumentError extends Error {
  readonly code = 'save_invalid';

  constructor(message = 'save_invalid') {
    super(message);
  }
}

/** Same allow-list the SQL helper private.accept_save_data enforces. */
export function acceptSaveData(input: unknown): SaveData {
  if (!input || typeof input !== 'object' || Array.isArray(input)) throw new SaveDocumentError();
  const data = input as Record<string, unknown>;
  const keys = Object.keys(data);
  if (keys.some((key) => !TOP_LEVEL.includes(key as (typeof TOP_LEVEL)[number]))) throw new SaveDocumentError();
  if (!TOP_LEVEL.every((key) => key in data)) throw new SaveDocumentError();
  if (JSON.stringify(data).length > 200000) throw new SaveDocumentError();

  const currencies = data.currencies;
  if (!currencies || typeof currencies !== 'object' || Array.isArray(currencies)) throw new SaveDocumentError();
  const currencyKeys = Object.keys(currencies as object);
  if (currencyKeys.some((key) => !CURRENCIES.includes(key as (typeof CURRENCIES)[number]))) throw new SaveDocumentError();
  const gold = (currencies as { gold?: unknown }).gold;
  const dust = (currencies as { forgeDust?: unknown }).forgeDust;
  if (!whole(gold) || !whole(dust)) throw new SaveDocumentError();
  if (gold > 1_000_000_000_000 || dust > 1_000_000_000_000) throw new SaveDocumentError();

  const hero = data.hero as {
    name?: unknown;
    level?: unknown;
    xp?: unknown;
    unspentPoints?: unknown;
    rules?: unknown;
  };
  if (typeof hero?.name !== 'string' || hero.name.length < 1 || hero.name.length > 24) throw new SaveDocumentError();
  if (!whole(hero.level) || hero.level < 1 || hero.level > 30) throw new SaveDocumentError();
  if (!whole(hero.xp) || hero.xp > 1_000_000) throw new SaveDocumentError();
  if (!whole(hero.unspentPoints) || hero.unspentPoints > 999) throw new SaveDocumentError();
  if (!Array.isArray(hero.rules) || hero.rules.length > 4) throw new SaveDocumentError();
  const heroBuild = (data.hero as { build?: unknown }).build;
  if (!heroBuild || typeof heroBuild !== 'object' || Array.isArray(heroBuild)) throw new SaveDocumentError();
  checkedBuild(heroBuild as BuildState);

  const gear = data.gear as { bag?: unknown };
  if (!Array.isArray(gear?.bag) || gear.bag.length > 24) throw new SaveDocumentError();

  const idle = data.idle as { snapshot?: unknown };
  if (idle?.snapshot !== null && idle?.snapshot !== undefined) {
    acceptSnapshot(idle.snapshot);
  }

  return input as SaveData;
}

export function starterSaveDocument(): SaveData {
  return acceptSaveData(createNewSave());
}

/**
 * The only fields the server will store from the client.
 * Currency, gear, progress, level, xp, and idle rates are left off on purpose.
 */
export function clientLoadoutPatch(data: SaveData): { hero: { name: string; build: BuildState; rules: Rule[] } } {
  const name = data.hero.name.trim();
  if (name.length < 1 || name.length > 24) throw new SaveDocumentError();
  return {
    hero: {
      name,
      build: checkedBuild(data.hero.build),
      rules: data.hero.rules.slice(0, 4).map((rule) => ({ ...rule })),
    },
  };
}

/** Server build, ranks, currency, gear, progress, level, xp, and unspent points stay put. */
export function applyClientLoadout(base: SaveData, incoming: SaveData): SaveData {
  const patch = clientLoadoutPatch(incoming);
  const next = structuredClone(base);
  next.hero.name = patch.hero.name;
  next.hero.rules = patch.hero.rules;
  return next;
}

function checkedBuild(build: BuildState): BuildState {
  const primary = checkedSpec(build.primary);
  const secondary = checkedSpec(build.secondary);
  const tertiary = checkedSpec(build.tertiary);
  if (!primary || !secondary || !tertiary) throw new SaveDocumentError();
  const slots: Record<SlotId, SubSpecId> = { primary, secondary, tertiary };
  if (new Set(SLOT_IDS.map((slot) => slots[slot])).size !== SLOT_IDS.length) throw new SaveDocumentError();
  if (!build.ranks || typeof build.ranks !== 'object' || Array.isArray(build.ranks)) throw new SaveDocumentError();
  const ranks: BuildState['ranks'] = {};
  for (const [key, value] of Object.entries(build.ranks)) {
    if (!SUBSPEC_IDS.includes(key as SubSpecId)) throw new SaveDocumentError();
    const slot = SLOT_IDS.find((name) => slots[name] === key);
    if (!slot) throw new SaveDocumentError();
    if (!whole(value) || value > SLOT_MAX_RANK[slot]) throw new SaveDocumentError();
    ranks[key as SubSpecId] = value;
  }
  return { primary, secondary, tertiary, ranks };
}

function checkedSpec(value: SubSpecId | null): SubSpecId | null {
  if (value === null) return null;
  if (!SUBSPEC_IDS.includes(value)) throw new SaveDocumentError();
  return value;
}

function acceptSnapshot(snapshot: unknown): void {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) throw new SaveDocumentError();
  const body = snapshot as { farmStageId?: unknown; capHours?: unknown; rates?: unknown };
  if (!whole(body.farmStageId)) throw new SaveDocumentError();
  if (body.capHours !== undefined && (!number(body.capHours) || body.capHours < 0 || body.capHours > 24)) {
    throw new SaveDocumentError();
  }
  if (!body.rates || typeof body.rates !== 'object' || Array.isArray(body.rates)) throw new SaveDocumentError();
  for (const key of Object.keys(body.rates as object)) {
    if (!RATE_KEYS.includes(key as (typeof RATE_KEYS)[number])) throw new SaveDocumentError();
    const value = (body.rates as Record<string, unknown>)[key];
    if (!number(value) || value < 0) throw new SaveDocumentError();
  }
}

function whole(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function number(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
